import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../state'
import * as modals from '../../../modals'

/** Displays and edits a space record's core "General" fields. */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.value.Component,
    ds.loader.Component,
    modals.roomData.Component,
  ],
  selector: 'atn-space-record-general',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** When true, the component is in read-only mode (e.g. room signed off). */
  public readonly isReadonlySg = ngCore.input(false, { alias: 'readonly' })

  /** @see {@link state.space.full.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** @see {@link state.space.roomTypes.Collection} */
  private readonly _roomTypesEc = ngCore.inject(
    state.space.roomTypes.Collection,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** Selected record */
  private readonly _recordSg = ngCore.computed(() =>
    this.recordsEc.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal(),
  )

  /** Whether the record is currently loading */
  public readonly isRecordLoadingSg = ngCore.computed(() =>
    this.recordsEc.plugins.currentEntityPlugin.feature.selectors
      .isLoading()
      .asSignal(),
  )

  /** Selected record name */
  public readonly recordNameSg = ngCore.computed(
    (record = this._recordSg()) => record?.name,
  )

  /** Selected record area (sqft) */
  public readonly recordAreaSg = ngCore.computed((record = this._recordSg()) =>
    record?.sqft != null ? Math.round(record.sqft) : undefined,
  )

  /** Selected room type */
  private readonly _roomTypeSg = ngCore.computed(
    (
      record = this._recordSg(),
      roomTypes = this._roomTypesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => roomTypes.find(rt => rt.id === record?.roomTypeId),
  )

  /** Selected room type display value */
  public readonly roomTypeSg = ngCore.computed(
    (roomType = this._roomTypeSg()) =>
      roomType ? `${roomType.code} - ${roomType.name}` : '-',
  )

  /** Selected room type category */
  public readonly roomCategorySg = ngCore.computed(
    (roomType = this._roomTypeSg()) =>
      roomType
        ? ents.extra.space.RoomType.Category.getLabel(roomType.category)
        : '-',
  )

  /** Selected record description */
  public readonly recordDescriptionSg = ngCore.computed(
    (record = this._recordSg()) => record?.description ?? '-',
  )

  /** Selected record comment */
  public readonly recordCommentSg = ngCore.computed(
    (record = this._recordSg()) => record?.comment,
  )

  /** Selected record anomalies */
  public readonly recordAnomaliesSg = ngCore.computed(
    (record = this._recordSg()) =>
      record?.anomalies?.length
        ? record.anomalies.map(ents.extra.space.Record.Anomaly.getLabel)
        : [],
  )

  /** Selected record ancestors */
  public readonly recordAncestorsSg = ngCore.computed(
    (
      record = this._recordSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) =>
      (record &&
        schema.getAncestorsOf(record.entityId).map(ancestor => ({
          name: ancestor.name,
          value:
            ancestor.id === record.entityId
              ? record.name
              : record.ancestors[ancestor.id]?.name,
        }))) ??
      [],
  )

  /** Edit record data */
  public editClickHandler(): void {
    this.recordsEc.plugins.editingPlugin.feature.actions.edit
      .from('RecordGeneral.Edit.Clicked')
      .dispatch({
        id: this._recordSg()?.id,
        makeCurrent: { discardPrevious: true },
      })
  }
}
